import { useCallback, useEffect, useState } from "react";
import useAuthGuard, { UseAuthGuardProps } from "@/hooks/useAuthGuard";
import { usePaywallDialogStore } from "@/stores/usePaywallDialogStore";
import { useFullPageLoaderOverlayStore } from "@/stores/useFullPageLoaderOverlayStore";
import { checkFeatureAccess, getPaywallFeatureConfig, isPaywallEnabled } from "@/config/paywall";
import type { SubscriptionStatusResponse } from "@/types/subscription";

interface UsePaywallGuardProps extends UseAuthGuardProps {
    paywallTitle?: string;
    paywallDescription?: string;
    actionBeforeRedirectToPlan?: () => void | Promise<void>;
    internalUrlToRedirectOnSuccess?: string;
    checkFeatureByPricingDataToUsePaywall?: boolean;
    skipPaywall?: boolean;
    showFullPageLoaderOverlay?: boolean;
    fullPageLoaderDelayMs?: number;
}


const usePaywallGuard = (props?: UsePaywallGuardProps) => {
    const {
        isAuthenticated,
        handleAction: authHandleAction,
        isLoading: authLoading,
        isDisabled,
        isHandleActionInProgress,
    } = useAuthGuard(props);

    const {
        setShowDialog: setShowUseFullPageLoaderDialog
    } = useFullPageLoaderOverlayStore();


    const [showPaywallDialog, setShowPaywallDialog] = useState(false);
    const [isCheckingAccount, setIsCheckingAccount] = useState(false);
    const [isPaywallActionInProgress, setIsPaywallActionInProgress] = useState(false);
    const [isPaywallPassed, setIsPaywallPassed] = useState(false);
    const [isPaywallClosedManually, setIsPaywallClosedManually] = useState(false);

    const shouldUsePaywall = (featureSlug?: string): boolean => {
        const featureConfig = getPaywallFeatureConfig(featureSlug);
        return isPaywallEnabled(featureSlug) && featureConfig.requiresSubscription;
    };

    // Determine if paywall should be used based on props
    let usePaywall = !props?.skipPaywall;
    if (usePaywall) {
        if (props?.checkFeatureByPricingDataToUsePaywall !== false) {
            usePaywall = shouldUsePaywall(props?.signInFeatureSlug);
        } else {
            usePaywall = isPaywallEnabled(props?.signInFeatureSlug);
        }
    }


    /**
     * Fetch user subscription status from API
     *
     * TODO: Add error handling for network failures
     * TODO: Add retry logic
     * TODO: Implement client-side caching
     */
    const fetchUserSubscriptionStatus = async (): Promise<boolean> => {
        setIsCheckingAccount(true);
        try {
            const res = await fetch("/api/user/subscription-status", {
                cache: "no-store",
            });

            if (!res.ok) {
                console.error('Failed to fetch subscription status:', res.statusText);
                setIsPaywallPassed(false);
                return false;
            }

            const data: SubscriptionStatusResponse = await res.json();

            // Check if user has access to the specific feature
            const resIsPaywallPassed = checkFeatureAccess(
                props?.signInFeatureSlug,
                data
            );

            setIsPaywallPassed(resIsPaywallPassed);
            return resIsPaywallPassed;
        } catch (error) {
            console.error('Error fetching subscription status:', error);
            setIsPaywallPassed(false);
            return false;
        } finally {
            setIsCheckingAccount(false);
        }
    };

    const handleAction = async (action?: () => void, signInModeType: "sign-in" | "sign-up" = "sign-in", isAlreadyAuthenticated = false) => {
        if (!isAuthenticated && !isAlreadyAuthenticated) {
            authHandleAction(async () => {
                await handleAction(action, signInModeType, true);
            }, signInModeType);
            return;
        }

        // If paywall should not be used, just execute the action directly
        if (!usePaywall) {
            if (action) {
                await action();
            }
            return;
        }

        const shouldShowLoaderOverlay = props?.showFullPageLoaderOverlay !== false;
        const loaderDelayMs = props?.fullPageLoaderDelayMs ?? 200;
        let loaderTimer: ReturnType<typeof setTimeout> | null = null;

        if (shouldShowLoaderOverlay) {
            loaderTimer = setTimeout(() => {
                setShowUseFullPageLoaderDialog(true);
            }, loaderDelayMs);
        }
        setIsPaywallClosedManually(false);
        setIsPaywallActionInProgress(true);

        // Fetch subscription status and check feature access
        const resIsPaywallPassed = await fetchUserSubscriptionStatus();

        if (loaderTimer) {
            clearTimeout(loaderTimer);
        }

        if (resIsPaywallPassed) {
            if (action) {
                await action();
            }
            if (shouldShowLoaderOverlay) {
                setShowUseFullPageLoaderDialog(false);
            }
            setIsPaywallActionInProgress(false);
        } else {
            setIsPaywallActionInProgress(false);
            if (shouldShowLoaderOverlay) {
                setShowUseFullPageLoaderDialog(false);
            }
            setShowPaywallDialog(true);
        }
    };

    const closePaywall = useCallback(() => {
        setShowUseFullPageLoaderDialog(false);
        setIsPaywallClosedManually(true);
        setShowPaywallDialog(false);
        setIsPaywallActionInProgress(false);
    }, [setShowUseFullPageLoaderDialog]);

    const { showPaywallDialog: showDialog, open: isPaywallDialogOpen } = usePaywallDialogStore();

    useEffect(() => {
        if (showPaywallDialog && !isPaywallDialogOpen) {
            const featureConfig = getPaywallFeatureConfig(props?.signInFeatureSlug);
            showDialog({
                open: true,
                featureSlug: props?.signInFeatureSlug,
                faqHref: undefined,
                internalUrlToRedirectOnSuccess: props?.internalUrlToRedirectOnSuccess,
                title: props?.paywallTitle || featureConfig.title,
                description: props?.paywallDescription || featureConfig.description,
                onClose: closePaywall,
                actionBeforeRedirectToPlan: props?.actionBeforeRedirectToPlan,
            });
        }
    }, [
        showPaywallDialog,
        isPaywallDialogOpen,
        showDialog,
        closePaywall,
        props?.paywallTitle,
        props?.paywallDescription,
        props?.signInFeatureSlug,
        props?.internalUrlToRedirectOnSuccess,
        props?.actionBeforeRedirectToPlan,
    ]);

    return {
        handleAction,
        paywallDialog: null,
        isCheckingAccount,
        isAuthenticated,
        isLoading: authLoading,
        isDisabled: isDisabled || authLoading || isPaywallActionInProgress,
        isHandleActionInProgress,
        isPaywallActionInProgress,
        isPaywallPassed,
        isPaywallClosedManually,
        shouldUsePaywall,
        usePaywall,
    };
};

export default usePaywallGuard;
